import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Moon, Sun, Menu, X, Globe, ChevronDown, ChevronRight,
  Shield, Server, Layers, Factory, Zap, Building2, Heart, Users,
  Briefcase, Award, Target, Compass
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/Button';
import styles from './Navigation.module.css';

// Icon mapping for mega menu items (both EN and DE labels)
const menuIconMap: Record<string, React.ReactNode> = {
  // Products - English
  'Strategic Software Asset Management': <Layers size={16} />,
  'Enterprise Governance & Digital Resilience': <Shield size={16} />,
  'Strategic ERP Modernization': <Server size={16} />,
  // Products - German
  'Strategisches Software-Asset-Management': <Layers size={16} />,
  'Enterprise Governance & Digitale Resilienz': <Shield size={16} />,
  'Strategische ERP-Modernisierung': <Server size={16} />,
  // Solutions - English
  'Change Management & Delivery': <Target size={16} />,
  'Sustainability & ESG Tech': <Zap size={16} />,
  'Security, Privacy & Governance': <Shield size={16} />,
  'Cloud & Platform Foundations': <Server size={16} />,
  'Software Engineering': <Layers size={16} />,
  'ERP & SAP': <Server size={16} />,
  // Solutions - German
  'Sicherheit, Datenschutz & Governance': <Shield size={16} />,
  'Cloud- & Plattform-Fundamenten': <Server size={16} />,
  'Softwareentwicklung': <Layers size={16} />,
  // Industries - English
  'Automotive': <Factory size={16} />,
  'Manufacturing & IIoT': <Factory size={16} />,
  'Logistics & Warehousing': <Factory size={16} />,
  'Fintech & Banking': <Building2 size={16} />,
  'Energy & Renewables': <Zap size={16} />,
  'Healthcare & MedTech': <Heart size={16} />,
  'Public Sector & Government': <Building2 size={16} />,
  'NGOs & Non-Profits': <Users size={16} />,
  // Industries - German
  'Fertigung & IIoT': <Factory size={16} />,
  'Logistik & Warehousing': <Factory size={16} />,
  'Fintech & Banken': <Building2 size={16} />,
  'Energie & Renewables': <Zap size={16} />,
  'Öffentlicher Sektor & Verwaltung': <Building2 size={16} />,
  // Company
  'Methodology & Approach': <Compass size={16} />,
  'Methodik & Ansatz': <Compass size={16} />,
  'Technology Partners': <Award size={16} />,
  'Technologie-Partner': <Award size={16} />,
  'Jobs & Culture': <Briefcase size={16} />,
  'Jobs & Kultur': <Briefcase size={16} />,
  'AI & Data Analytics': <Zap size={16} />,
};

interface NavItem {
  key: string;
  label: string;
  href?: string;
  megaMenu?: {
    tagline: string;
    subtitle: string;
    categories: {
      title: string;
      items: { label: string; href: string }[];
    }[];
    cta?: {
      title: string;
      subtitle: string;
      description: string;
      buttonText: React.ReactNode;
      buttonHref: string;
    };
  };
}

const getNavItems = (lang: 'de' | 'en'): NavItem[] => {
  const t = {
    de: {
      products: 'Produkte',
      solutions: 'Lösungen',
      industries: 'Branchen',
      company: 'Über Uns',
      trust: 'Vertrauen & Compliance',
      insights: 'Einblicke',
      contact: 'Kontakt',
      ctaButton: 'Kontakt aufnehmen',
      productsTagline: 'STRATEGISCHES PORTFOLIO',
      productsSubtitle: 'Programme für Software-Assets, Governance und ERP-Modernisierung.',
      productsSuite: 'KERNPROGRAMME',
      productsCtaTitle: 'Strategisches Assessment',
      productsCtaSubtitle: 'Benötigen Sie eine Portfolio-Analyse?',
      productsCtaDesc: 'Wir liefern eine belastbare Entscheidungsbasis für Asset, Governance und ERP.',
      // Products menu items
      productLicenseDefense: 'Strategisches Software-Asset-Management',
      productOfficerShield: 'Enterprise Governance & Digitale Resilienz',
      productCleanCore: 'Strategische ERP-Modernisierung',
      solutionsTagline: 'ENTERPRISE DELIVERY',
      solutionsSubtitle: 'Beratung und Delivery für strategische Enterprise-Programme.',
      transformationStrategy: 'STRATEGIE & GOVERNANCE',
      secureFoundations: 'ENTERPRISE PLATFORMS',
      intelligentEnterprise: 'DIGITAL WORKPLACE & DATA',
      aiAutomation: 'AI & Data Analytics',
      // Solutions menu items
      changeManagement: 'Change Management & Delivery',
      strategyAndTransformation: 'Strategy & Transformation',
      sustainability: 'Sustainability & ESG Tech',
      securityPrivacy: 'Sicherheit, Datenschutz & Governance',
      cloudPlatform: 'Cloud- & Plattform-Fundamenten',
      softwareEngineering: 'Softwareentwicklung',
      erpSap: 'ERP & SAP',
      solutionsCtaTitle: 'STRATEGISCHE ORIENTIERUNG',
      solutionsCtaSubtitle: 'Sie planen ein Enterprise-Programm?',
      solutionsCtaDesc: 'Buchen Sie ein Executive Briefing – wir strukturieren Scope, Risiko und Roadmap.',
      solutionsCtaButton: 'Strategische Beratung',
      industriesTagline: 'MAẞGESCHNEIDERTE EXPERTISE',
      industriesSubtitle: 'Wir sprechen Ihre Sprache – regulatorisch und operativ.',
      manufacturingMobility: 'MANUFACTURING & MOBILITY',
      automotive: 'Automotive',
      manufacturing: 'Fertigung & IIoT',
      logistics: 'Logistik & Warehousing',
      criticalInfraFinance: 'KRITISCHE INFRASTRUKTUR & FINANZEN',
      energy: 'Energie & Renewables',
      fintech: 'Fintech & Banken',
      publicSocialImpact: 'ÖFFENTLICH & SOZIAL',
      publicSector: 'Öffentlicher Sektor & Verwaltung',
      healthcare: 'Healthcare & MedTech',
      ngo: 'NGOs & Non-Profits',
      industriesCtaTitle: 'BRANCHEN-EXPERTISE',
      industriesCtaSubtitle: 'Spezifische Anforderungen?',
      industriesCtaDesc: 'Unsere Experten kennen die Regularien und Standards Ihrer Branche (KRITIS, TISAX, BAIT/VAIT).',
      industriesCtaButton: 'Experten sprechen (30 Min)',
      companyTagline: 'DER INTEGROVA UNTERSCHIED',
      companySubtitle: 'Exzellenz durch Erfahrung und Innovation.',
      theCompany: 'DAS UNTERNEHMEN',
      aboutUs: 'Über Uns',
      ourValues: 'Unsere Werte',
      excellence: 'EXZELLENZ',
      methodology: 'Methodik & Ansatz',
      techPartners: 'Technologie-Partner',
      career: 'KARRIERE',
      jobsCulture: 'Jobs & Kultur',
      companyCtaTitle: 'WERDEN SIE TEIL DES TEAMS',
      companyCtaSubtitle: 'Lust auf Impact?',
      companyCtaDesc: 'Wir suchen Talente, die die digitale Zukunft mitgestalten wollen.',
      companyCtaButton: 'Karriere starten',
    },
    en: {
      products: 'Products',
      solutions: 'Solutions',
      industries: 'Industries',
      company: 'About Us',
      trust: 'Trust & Compliance',
      insights: 'Insights',
      contact: 'Contact',
      ctaButton: 'Get in Touch',
      productsTagline: 'STRATEGIC PORTFOLIO',
      productsSubtitle: 'Programs for software assets, governance, and ERP modernization.',
      productsSuite: 'CORE PROGRAMS',
      productsCtaTitle: 'Strategic assessment',
      productsCtaSubtitle: 'Need a portfolio review?',
      productsCtaDesc: 'We deliver decision-ready baselines for assets, governance, and ERP.',
      // Products menu items
      productLicenseDefense: 'Strategic Software Asset Management',
      productOfficerShield: 'Enterprise Governance & Digital Resilience',
      productCleanCore: 'Strategic ERP Modernization',
      solutionsTagline: 'ENTERPRISE DELIVERY',
      solutionsSubtitle: 'Advisory and delivery for strategic enterprise programs.',
      transformationStrategy: 'STRATEGY & GOVERNANCE',
      secureFoundations: 'ENTERPRISE PLATFORMS',
      intelligentEnterprise: 'DIGITAL WORKPLACE & DATA',
      aiAutomation: 'AI & Data Analytics',
      // Solutions menu items
      changeManagement: 'Change Management & Delivery',
      strategyAndTransformation: 'Strategy & Transformation',
      sustainability: 'Sustainability & ESG Tech',
      securityPrivacy: 'Security, Privacy & Governance',
      cloudPlatform: 'Cloud & Platform Foundations',
      softwareEngineering: 'Software Engineering',
      erpSap: 'ERP & SAP',
      solutionsCtaTitle: 'STRATEGIC ALIGNMENT',
      solutionsCtaSubtitle: 'Planning an enterprise program?',
      solutionsCtaDesc: "Book an executive briefing to align scope, risk, and roadmap.",
      solutionsCtaButton: 'Strategic consultation',
      industriesTagline: 'TAILORED EXPERTISE',
      industriesSubtitle: 'We speak your language – regulatory and operational.',
      manufacturingMobility: 'MANUFACTURING & MOBILITY',
      automotive: 'Automotive',
      manufacturing: 'Manufacturing & IIoT',
      logistics: 'Logistics & Warehousing',
      criticalInfraFinance: 'CRITICAL INFRASTRUCTURE & FINANCE',
      energy: 'Energy & Renewables',
      fintech: 'Fintech & Banking',
      publicSocialImpact: 'PUBLIC & SOCIAL IMPACT',
      publicSector: 'Public Sector & Government',
      healthcare: 'Healthcare & MedTech',
      ngo: 'NGOs & Non-Profits',
      industriesCtaTitle: 'INDUSTRY EXPERTISE',
      industriesCtaSubtitle: 'Specific requirements?',
      industriesCtaDesc: 'Our experts know the regulations and standards of your industry (KRITIS, TISAX, BAIT/VAIT).',
      industriesCtaButton: 'Talk to Experts (30 min)',
      companyTagline: 'THE INTEGROVA DIFFERENCE',
      companySubtitle: 'Excellence through experience and innovation.',
      theCompany: 'THE COMPANY',
      aboutUs: 'About Us',
      ourValues: 'Our Values',
      excellence: 'EXCELLENCE',
      methodology: 'Methodology & Approach',
      techPartners: 'Technology Partners',
      career: 'CAREERS',
      jobsCulture: 'Jobs & Culture',
      companyCtaTitle: 'JOIN THE TEAM',
      companyCtaSubtitle: 'Ready for impact?',
      companyCtaDesc: "We're looking for talent who want to shape the digital future.",
      companyCtaButton: 'Start Your Career',
    },
  };

  const tr = t[lang];

  return [
    {
      key: 'products',
      label: tr.products,
      href: '/#products',
      megaMenu: {
        tagline: tr.productsTagline,
        subtitle: tr.productsSubtitle,
        categories: [
          {
            title: tr.productsSuite,
            items: [
              { label: tr.productLicenseDefense, href: '/products/license-defense' },
              { label: tr.productOfficerShield, href: '/products/officer-shield' },
              { label: tr.productCleanCore, href: '/products/clean-core-surgeon' },
            ],
          },
        ],
        cta: {
          title: tr.productsCtaTitle,
          subtitle: tr.productsCtaSubtitle,
          description: tr.productsCtaDesc,
          buttonText: lang === 'en' ? 'Start strategic assessment' : 'Strategisches Assessment starten',
          buttonHref: '/assessment',
        },
      },
    },
    {
      key: 'solutions',
      label: tr.solutions,
      href: '/services',
      megaMenu: {
        tagline: tr.solutionsTagline,
        subtitle: tr.solutionsSubtitle,
        categories: [
          {
            title: tr.transformationStrategy,
            items: [
              { label: tr.strategyAndTransformation, href: '/services/strategy-and-transformation' },
              { label: tr.changeManagement, href: '/services/change-management-delivery' },
              { label: tr.sustainability, href: '/services/sustainability-esg-tech' },
            ]
          },
          {
            title: tr.secureFoundations,
            items: [
              { label: tr.securityPrivacy, href: '/services/security-privacy-governance' },
              { label: tr.cloudPlatform, href: '/services/cloud-platform-foundations' },
            ]
          },
          {
            title: tr.intelligentEnterprise,
            items: [
              { label: tr.aiAutomation, href: '/services/ai-automation' },
              { label: tr.softwareEngineering, href: '/services/software-engineering' },
              { label: tr.erpSap, href: '/services/erp-sap' },
            ]
          },
        ],
        cta: {
          title: tr.solutionsCtaTitle,
          subtitle: tr.solutionsCtaSubtitle,
          description: tr.solutionsCtaDesc,
          buttonText: lang === 'en' ? 'Book a 30-minute consultation' : '30-Minuten Beratung buchen',
          buttonHref: '/consultation',
        }
      }
    },
    {
      key: 'industries',
      label: tr.industries,
      href: '/industries',
      megaMenu: {
        tagline: tr.industriesTagline,
        subtitle: tr.industriesSubtitle,
        categories: [
          {
            title: tr.manufacturingMobility,
            items: [
              { label: tr.automotive, href: '/industries/automotive' },
              { label: tr.manufacturing, href: '/industries/fertigung-iiot' },
              { label: tr.logistics, href: '/industries/logistik-warehousing' },
            ]
          },
          {
            title: tr.criticalInfraFinance,
            items: [
              { label: tr.energy, href: '/industries/energie-renewables' },
              { label: tr.fintech, href: '/industries/fintech' },
            ]
          },
          {
            title: tr.publicSocialImpact,
            items: [
              { label: tr.publicSector, href: '/industries/public-sector' },
              { label: tr.healthcare, href: '/industries/healthcare-medtech' },
              { label: tr.ngo, href: '/industries/ngo-security' },
            ]
          },
        ],
        cta: {
          title: tr.industriesCtaTitle,
          subtitle: tr.industriesCtaSubtitle,
          description: tr.industriesCtaDesc,
          buttonText: lang === 'en' ? 'Talk to Experts (30 min)' : 'Experten sprechen (30 Min)',
          buttonHref: '/consultation',
        }
      }
    },
    {
      key: 'trust',
      label: tr.trust,
      href: '/trust',
    },
    {
      key: 'insights',
      label: tr.insights,
      href: '/insights',
    },
    {
      key: 'company',
      label: tr.company,
      href: '/company',
    },
    { key: 'contact', label: tr.contact, href: '/contact' },
  ];
};

export const Navigation: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const langMenuRef = React.useRef<HTMLDivElement>(null);

  // Close language menu on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };

    if (isLangDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLangDropdownOpen]);

  const handleMouseEnter = (key: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActiveDropdown(key);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150); // 150ms delay
  };

  const navItems = useMemo(() => getNavItems(language), [language]);
  const ctaButtonText = t.hero.cta1;

  // Check if a nav item is active based on current path
  const isNavItemActive = (item: NavItem): boolean => {
    if (!item.href) return false;
    const currentPath = location.pathname;
    if (item.href === '/#products') return currentPath === '/';
    if (item.href.startsWith('/#')) return false;
    return currentPath.startsWith(item.href);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    if (href.startsWith('/#')) {
      const elementId = href.substring(2);
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => {
          const el = document.getElementById(elementId);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      navigate(href);
    }
  };

  const handleDropdownBlur = (event: React.FocusEvent<HTMLLIElement>) => {
    const nextFocused = event.relatedTarget as Node | null;
    if (nextFocused && event.currentTarget.contains(nextFocused)) {
      return;
    }
    setActiveDropdown(null);
  };

  return (
    <>
      <motion.nav
        className={`${styles.nav} ${isScrolled ? styles['nav--scrolled'] : ''} `}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
      >
        <div className={styles.nav__container}>
          {/* Logo */}
          <Link to="/" className={styles.nav__logo}>
            <img src="/logo_icon_new.png" alt="Integrova AI Icon" className={styles.nav__logoIcon} />
            {theme === 'dark' ? (
              <img
                src="/logo_modern_v2.svg"
                alt="Integrova AI"
                className={styles.nav__logoImage}
              />
            ) : (
              <img
                src="/logo_modern_v2_light.svg"
                alt="Integrova AI"
                className={styles.nav__logoImage}
              />
            )}
          </Link>

          {/* Desktop Navigation */}
          <ul className={styles.nav__links}>
            {navItems.map((item) => (
              <li
                key={item.key}
                className={styles.nav__item}
                onMouseEnter={() => item.megaMenu && handleMouseEnter(item.key)}
                onMouseLeave={handleMouseLeave}
                onFocus={() => item.megaMenu && handleMouseEnter(item.key)}
                onBlur={handleDropdownBlur}
              >
                {item.href ? (
                  <a
                    href={item.href}
                    className={`${styles.nav__link} ${isNavItemActive(item) ? styles['nav__link--active'] : ''} ${item.megaMenu ? styles['nav__link--hasDropdown'] : ''}`}
                    onClick={(e) => {
                      if (item.megaMenu && activeDropdown !== item.key) {
                        e.preventDefault();
                        setActiveDropdown(item.key);
                      } else {
                        e.preventDefault();
                        handleNavClick(item.href!);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        setActiveDropdown(null);
                      }
                      if (item.megaMenu && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        setActiveDropdown(activeDropdown === item.key ? null : item.key);
                      }
                    }}
                    aria-haspopup={item.megaMenu ? "true" : undefined}
                    aria-expanded={item.megaMenu ? activeDropdown === item.key : undefined}
                  >
                    {item.label}
                    {item.megaMenu && <ChevronDown size={14} className={`${styles.nav__chevron} ${activeDropdown === item.key ? styles['nav__chevron--open'] : ''}`} />}
                  </a>
                ) : (
                  <button
                    className={styles.nav__link}
                    aria-haspopup="true"
                    aria-expanded={activeDropdown === item.key}
                    aria-controls={`mega - ${item.key} `}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        setActiveDropdown(null);
                      }
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setActiveDropdown(activeDropdown === item.key ? null : item.key);
                      }
                    }}
                  >
                    {item.label}
                    {item.megaMenu && <ChevronDown size={14} className={`${styles.nav__chevron} ${activeDropdown === item.key ? styles['nav__chevron--open'] : ''}`} />}
                  </button>
                )}

                {/* Mega Menu */}
                {item.megaMenu && (
                  <AnimatePresence>
                    {activeDropdown === item.key && (
                      <motion.div
                        className={styles.nav__megaMenu}
                        id={`mega - ${item.key} `}
                        initial={{ opacity: 0, y: 10, x: '-50%' }}
                        animate={{ opacity: 1, y: 0, x: '-50%' }}
                        exit={{ opacity: 0, y: 10, x: '-50%' }}
                        transition={{ duration: 0.2 }}
                        onMouseEnter={() => handleMouseEnter(item.key)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <div className={styles.nav__megaMenuContent}>
                          {/* Header */}
                          <div className={styles.nav__megaMenuHeader}>
                            <span className={styles.nav__megaMenuTagline}>{item.megaMenu.tagline}</span>
                            <p className={styles.nav__megaMenuSubtitle}>{item.megaMenu.subtitle}</p>
                          </div>

                          {/* Categories Grid */}
                          <div className={styles.nav__megaMenuGrid}>
                            {item.megaMenu.categories.map((category) => (
                              <div key={category.title} className={styles.nav__megaMenuCategory}>
                                <span className={styles.nav__megaMenuCategoryTitle}>{category.title}</span>
                                <ul className={styles.nav__megaMenuItems}>
                                  {category.items.map((subItem) => (
                                    <li key={subItem.href}>
                                      <Link
                                        to={subItem.href}
                                        className={styles.nav__megaMenuItem}
                                        onClick={() => setActiveDropdown(null)}
                                      >
                                        <span className={styles.nav__megaMenuItemIcon}>
                                          {menuIconMap[subItem.label] || <Layers size={16} />}
                                        </span>
                                        {subItem.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>

                          {/* CTA Sidebar */}
                          {item.megaMenu.cta && (
                            <div className={styles.nav__megaMenuCta}>
                              <span className={styles.nav__megaMenuCtaTagline}>{item.megaMenu.cta.title}</span>
                              <h4 className={styles.nav__megaMenuCtaTitle}>{item.megaMenu.cta.subtitle}</h4>
                              <p className={styles.nav__megaMenuCtaDesc}>{item.megaMenu.cta.description}</p>
                              <Button
                                variant="primary"
                                onClick={() => handleNavClick(item.megaMenu!.cta!.buttonHref)}
                              >
                                {item.megaMenu.cta.buttonText}
                              </Button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </li>
            ))}
          </ul>

          {/* Controls */}
          <div className={styles.nav__controls}>
            {/* Language Switcher */}
            <div className={styles.nav__langWrapper} ref={langMenuRef}>
              <button
                className={styles.nav__langToggle}
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                aria-label="Select language"
              >
                <Globe size={18} />
                <span>{language.toUpperCase()}</span>
              </button>
              <AnimatePresence>
                {isLangDropdownOpen && (
                  <motion.div
                    className={styles.nav__langDropdown}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <button
                      className={`${styles.nav__langOption} ${language === 'de' ? styles['nav__langOption--active'] : ''} `}
                      onClick={() => {
                        setLanguage('de');
                        setIsLangDropdownOpen(false);
                      }}
                    >
                      🇩🇪 Deutsch
                    </button>
                    <button
                      className={`${styles.nav__langOption} ${language === 'en' ? styles['nav__langOption--active'] : ''} `}
                      onClick={() => {
                        setLanguage('en');
                        setIsLangDropdownOpen(false);
                      }}
                    >
                      🇬🇧 English
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Theme Toggle */}
            <button
              className={styles.nav__themeToggle}
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              <AnimatePresence mode="wait">
                {theme === 'dark' ? (
                  <motion.div
                    key="sun"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Sun size={20} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="moon"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Moon size={20} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>

            {/* CTA Button */}
            <div className={styles.nav__ctaWrapper}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleNavClick('/#contact')}
              >
                {ctaButtonText}
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className={styles.nav__mobileToggle}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              {/* Backdrop overlay */}
              <motion.div
                className={styles.nav__mobileBackdrop}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <motion.div
                className={styles.nav__mobileMenu}
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              >
                <ul className={styles.nav__mobileLinks}>
                  {navItems.map((item, index) => (
                    <motion.li
                      key={item.key}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                      className={styles.nav__mobileItem}
                    >
                      {item.megaMenu ? (
                        <>
                          <button
                            className={`${styles.nav__mobileLink} ${styles.nav__mobileAccordionTrigger} `}
                            onClick={() => setMobileAccordion(mobileAccordion === item.key ? null : item.key)}
                            aria-expanded={mobileAccordion === item.key}
                          >
                            {item.label}
                            <ChevronRight
                              size={18}
                              className={`${styles.nav__mobileChevron} ${mobileAccordion === item.key ? styles['nav__mobileChevron--open'] : ''} `}
                            />
                          </button>
                          <AnimatePresence>
                            {mobileAccordion === item.key && (
                              <motion.div
                                className={styles.nav__mobileAccordion}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                {item.megaMenu.categories.map((category) => (
                                  <div key={category.title} className={styles.nav__mobileAccordionCategory}>
                                    <span className={styles.nav__mobileAccordionTitle}>{category.title}</span>
                                    {category.items.map((subItem) => (
                                      <Link
                                        key={subItem.href}
                                        to={subItem.href}
                                        className={styles.nav__mobileAccordionLink}
                                        onClick={() => {
                                          setIsMobileMenuOpen(false);
                                          setMobileAccordion(null);
                                        }}
                                      >
                                        {menuIconMap[subItem.label] || <Layers size={14} />}
                                        {subItem.label}
                                      </Link>
                                    ))}
                                  </div>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        <a
                          href={item.href || '#'}
                          className={`${styles.nav__mobileLink} ${isNavItemActive(item) ? styles['nav__mobileLink--active'] : ''} `}
                          onClick={(e) => {
                            e.preventDefault();
                            if (item.href) handleNavClick(item.href);
                          }}
                        >
                          {item.label}
                        </a>
                      )}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Mobile sticky CTA */}
      <div className={styles.nav__mobileCtaSticky}>
        <Button variant="primary" size="lg" to="/consultation" fullWidth>
          {ctaButtonText}
        </Button>
      </div>

      {/* Desktop sticky CTA - appears after scrolling */}
      {isScrolled && (
        <motion.div
          className={styles.nav__desktopCtaSticky}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.2 }}
        >
          <Button variant="primary" size="md" to="/consultation">
            {language === 'de' ? 'Kontakt' : 'Contact'}
          </Button>
        </motion.div>
      )}
    </>
  );
};

export default Navigation;
